import { usePageCrumbs } from '../lib/crumbs.ts';
import { Activity, CirclePause, FolderGit2, GitMerge, Play, RotateCcw } from '@orgus/ui/icons';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { PROJECT_PROTOCOL_ADAPTER, pipelineKey } from '@orgus/protocol';
import type { PipelineItem } from '@orgus/protocol';
import { DataGate, PageBody } from '../components/shell/PageBody.tsx';
import { Button, ConfirmDialog, EmptyState, ErrorState, Skeleton, toast, useNow } from '@orgus/ui';
import { refreshProjectProtocol, useProjectProtocol } from '../features/project-health/api.ts';
import { WaitingForBusy } from '../features/project-health/Holds.tsx';
import { boundBoardActions, notReportingLabel, pipelineNotReporting, protocolRunId, reportFor, roleBindings } from '../features/project-health/model.ts';
import { openDeclaredAction } from '../features/project-health/store.ts';
import { EnvironmentsView } from '../features/landing/EnvironmentsView.tsx';
import { LandingQueueView } from '../features/landing/LandingQueueView.tsx';
import { PipelinesTabs } from '../features/landing/PipelinesTabs.tsx';
import { useLanding } from '../features/landing/api.ts';
import { needsAttention } from '../features/landing/model.ts';
import { Changes } from '../features/pipelines/Changes.tsx';
import { PipelineFigures } from '../features/pipelines/Figures.tsx';
import { LogDialog } from '../features/pipelines/LogDialog.tsx';
import { PipelineHeader, actionCopy, availableActions } from '../features/pipelines/PipelineHeader.tsx';
import type { PipelineAction } from '../features/pipelines/PipelineHeader.tsx';
import { boardColumns, findItem, flightSummary } from '../features/pipelines/model.ts';
import { OutOfScopeNotice, useOutOfScope } from '../features/teams/OutOfScope.tsx';
import { runAction } from '../lib/api.ts';
import { useRegisterCommands } from '../lib/commands.ts';
import type { Command } from '../lib/commands.ts';
import { useOrgus, usePipelines } from '../lib/store.ts';
import { WhenRuns } from '../features/server/ModuleAbsent.tsx';

function PipelinesSkeleton() {
  return (
    <div className="flex flex-col gap-6" role="status" aria-label="Loading pipelines">
      <Skeleton className="h-24 rounded-card" />
      <Skeleton className="h-80 rounded-card" />
    </div>
  );
}

/**
 * Pipelines: three tabs under one rail entry, in one header row with the view's own controls.
 * Pipelines (one strip of figures, then the changes: now, history, resubmitted), Landing queue
 * and Environments (features/landing). The tab is the path, the pipeline `?pipeline=`, the list
 * `?list=` and an open item `?item=`, so every view has an address the breadcrumb can name.
 */
export default function Pipelines() {
  const view = (useParams()['*'] ?? '').split('/')[0];
  usePageCrumbs(view === 'landing' ? [{ label: 'Landing queue' }] : view === 'environments' ? [{ label: 'Environments' }] : []);
  // Every view is wide, so the header row does not jump between tabs; each puts its own controls
  // in that row (PipelinesTabs).
  if (view === 'landing' || view === 'environments') {
    return (
      <PageBody wide>
        <WhenRuns module="landing" feature={view === 'landing' ? 'Landing queues' : 'Environments'} plural instead="Pipelines lists each project's CI runs and stages today." size="page">
          {view === 'landing' ? <LandingQueueView /> : <EnvironmentsView />}
        </WhenRuns>
      </PageBody>
    );
  }
  return <AdapterPipelines />;
}

/** The adapters' pipelines (qawl-land and others): read from the machines that run them. */
function AdapterPipelines() {
  const pipelines = usePipelines();
  const machines = useOrgus((s) => s.machines);
  const projects = useOrgus((s) => s.projects);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [confirm, setConfirm] = useState<{ kind: PipelineAction; key: string } | null>(null);

  const wanted = params.get('pipeline');
  const selected = useMemo(() => {
    const byKey = pipelines.find((p) => pipelineKey(p) === wanted);
    if (byKey) return byKey;
    // Default: the busiest running pipeline.
    return [...pipelines].sort((a, b) => Number(b.state === 'running') - Number(a.state === 'running') || b.items.length - a.items.length)[0];
  }, [pipelines, wanted]);
  const selectedKey = selected ? pipelineKey(selected) : '';
  // A link to a pipeline the chosen workspace hides: say so, rather than quietly showing another.
  const hidden = useOutOfScope(wanted && !pipelines.some((p) => pipelineKey(p) === wanted) ? { pipeline: wanted } : null);
  const itemId = params.get('item');
  // A pipeline read through the project protocol: its actions are the project's declared ones
  // (bound by role), and a pipeline.json that stopped reporting says since when.
  const pp = useProjectProtocol().state;
  const now = useNow(5000);
  // With no project pipeline, the landing queue can still be busy: the empty state says so, not "nothing yet".
  const landing = useLanding().state;
  const landingNeeds = landing ? needsAttention(landing.landings, now).length : 0;
  const landingFlight = landing ? landing.queues.reduce((n, q) => n + q.order.length + (q.activeId ? 1 : 0), 0) : 0;
  const report = selected ? reportFor(pp, selected.projectId, selected.machineId) : undefined;
  const isProtocol = selected?.adapter === PROJECT_PROTOCOL_ADAPTER || report?.pipelineSource === 'protocol';
  const protocol = isProtocol ? report : undefined;
  // A protocol pipeline's buttons run a declared project command: only ever through its
  // confirmation dialog, so with no report at hand it offers none.
  const bound = isProtocol && selected ? boundBoardActions(selected.state, protocol) : undefined;
  const silent = pipelineNotReporting(protocol);
  const silentLabel = silent ? notReportingLabel(silent.since, now) : null;
  const act = useCallback(
    (kind: PipelineAction, key: string) => {
      const decl = protocol ? roleBindings(protocol)[kind] : undefined;
      if (decl && protocol) openDeclaredAction({ projectId: protocol.projectId, machineId: protocol.machineId, actionId: decl.id });
      else if (!isProtocol) setConfirm({ kind, key });
    },
    [protocol, isProtocol],
  );
  const openItem = selected && itemId ? findItem(selected, itemId) : undefined;
  const cols = useMemo(() => (selected ? boardColumns(selected, now) : []), [selected, now]);
  const flight = useMemo(() => flightSummary(cols), [cols]);

  const set = useCallback(
    (patch: Record<string, string | null>) =>
      setParams(
        (p) => {
          const n = new URLSearchParams(p);
          for (const [k, v] of Object.entries(patch)) {
            if (v == null) n.delete(k);
            else n.set(k, v);
          }
          return n;
        },
        { replace: true },
      ),
    [setParams],
  );
  const select = useCallback((key: string) => set({ pipeline: key, item: null }), [set]);
  const open = useCallback((i: PipelineItem) => set({ pipeline: selectedKey, item: i.id }), [set, selectedKey]);

  const confirmPipeline = confirm ? pipelines.find((p) => pipelineKey(p) === confirm.key) : undefined;
  const confirmMachine = confirmPipeline ? (machines[confirmPipeline.machineId]?.name ?? 'its machine') : '';
  const copy = confirm && confirmPipeline ? actionCopy(confirm.kind, confirmPipeline, confirmMachine) : null;

  const cmds = useMemo<Command[]>(() => {
    const out: Command[] = pipelines.map((p, i) => ({
      id: `pipeline.open:${pipelineKey(p)}`,
      title: `Open ${projects[p.projectId]?.name ?? 'pipeline'} ${p.title.toLowerCase()}`,
      subtitle: `on ${machines[p.machineId]?.name ?? 'unknown'}`,
      group: 'Pipelines',
      icon: Activity,
      order: i,
      perform: () => select(pipelineKey(p)),
    }));
    if (selected) {
      const icon = { 'pipeline.pause': CirclePause, 'pipeline.resume': Play, 'pipeline.restart': RotateCcw } as const;
      for (const k of bound ?? availableActions(selected)) {
        out.push({
          id: `${k}:${selectedKey}`,
          title: `${k === 'pipeline.pause' ? 'Pause' : k === 'pipeline.resume' ? 'Resume' : 'Restart'} the ${selected.title.toLowerCase()}`,
          subtitle: `${projects[selected.projectId]?.name ?? ''} on ${machines[selected.machineId]?.name ?? 'unknown'}`,
          group: 'Pipelines',
          icon: icon[k],
          order: 50,
          perform: () => act(k, selectedKey),
        });
      }
    }
    return out;
  }, [pipelines, projects, machines, select, selected, selectedKey, bound, act]);
  useRegisterCommands(cmds, [cmds]);

  return (
    <PageBody wide>
      <PipelinesTabs>
        {selected && (
          <PipelineHeader
            pipeline={selected}
            machine={machines[selected.machineId]}
            project={projects[selected.projectId]}
            onAction={(kind) => act(kind, selectedKey)}
            pipelines={pipelines}
            projectName={(id) => projects[id]?.name ?? 'Unknown project'}
            machineName={(id) => machines[id]?.name ?? 'an unknown machine'}
            onSelect={select}
            actions={bound}
            notReporting={silentLabel}
          />
        )}
      </PipelinesTabs>
      <DataGate skeleton={<PipelinesSkeleton />}>
        {hidden && <OutOfScopeNotice what="pipeline" info={hidden} className="mb-4" />}
        {!selected ? (
          <div className="motion-arrive rounded-card border border-border bg-surface shadow-elevation-flat">
            {landingNeeds + landingFlight > 0 ? (
            <EmptyState
              size="page"
              icon={<GitMerge />}
              title="Changes are landing through the landing queue"
              description={`${landingNeeds ? `${landingNeeds} ${landingNeeds === 1 ? 'landing needs' : 'landings need'} you` : ''}${landingNeeds && landingFlight ? ', ' : ''}${landingFlight ? `${landingFlight} queued or in progress` : ''}. A project's own pipeline shows here once it reports one.`}
              action={
                <Button variant="primary" icon={<GitMerge />} onClick={() => navigate('/pipelines/landing')}>
                  Open the landing queue
                </Button>
              }
            />
            ) : (
            <EmptyState
              size="page"
              icon={<Activity />}
              title="No pipelines yet"
              description="Pipelines show here once a watched project has one, such as a landing queue."
              action={
                <Button variant="primary" icon={<FolderGit2 />} onClick={() => navigate('/projects')}>
                  Go to Projects
                </Button>
              }
            />
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {silent ? (
              <ErrorState
                variant="inline"
                error={{
                  what: `The ${selected.title.toLowerCase()} is not reporting.`,
                  why: silent.reason ? `pipeline.json: ${silent.reason.charAt(0).toLowerCase()}${silent.reason.slice(1)}.` : 'Its pipeline.json was not rewritten in time.',
                  next: `Check that ${projects[selected.projectId]?.name ?? 'the project'}'s runner is running on ${machines[selected.machineId]?.name ?? 'its machine'}.`,
                }}
              />
            ) : (
              (selected.state === 'error' || selected.state === 'down' || selected.error) && (
                <ErrorState
                  variant="inline"
                  error={{
                    what: selected.state === 'down' ? `The ${selected.title.toLowerCase()} is not running.` : `The ${selected.title.toLowerCase()} reported a problem.`,
                    why: selected.error ?? 'The machine did not say why.',
                    next: (bound ?? availableActions(selected)).includes('pipeline.restart') ? 'Restart it, or check the machine.' : 'Check the machine it runs on.',
                  }}
                />
              )
            )}
            <PipelineFigures pipeline={selected} machine={machines[selected.machineId]} flight={flight} />
            <Changes pipeline={selected} onOpen={open} activeItemId={itemId} extra={<WaitingForBusy projectId={selected.projectId} machineId={selected.machineId} />} />
          </div>
        )}
      </DataGate>

      <LogDialog pipeline={selected} item={openItem} onClose={() => set({ item: null })} onOpenItem={open} />

      <ConfirmDialog
        open={!!confirm && !!copy}
        onOpenChange={(v) => !v && setConfirm(null)}
        title={copy?.title ?? ''}
        description={copy?.description ?? ''}
        confirmLabel={copy?.confirm ?? 'Continue'}
        tone={confirm?.kind === 'pipeline.restart' ? 'danger' : 'primary'}
        onConfirm={async () => {
          if (!confirm || !confirmPipeline) return;
          try {
            await runAction(confirmPipeline.machineId, confirm.kind, { projectId: confirmPipeline.projectId });
          } catch (e) {
            // The hub found the project protocol behind this pipeline: it requested the declared
            // action instead and names the run that waits for a person. Open that confirmation.
            const runId = protocolRunId(e);
            if (!runId) throw e;
            const s = await refreshProjectProtocol().catch(() => undefined);
            const run = s?.runs.find((r) => r.id === runId);
            if (!run) throw e;
            openDeclaredAction({ projectId: run.projectId, machineId: run.machineId, actionId: run.actionId, runId });
            return;
          }
          const verb = confirm.kind === 'pipeline.pause' ? 'Paused' : confirm.kind === 'pipeline.resume' ? 'Resumed' : 'Restarted';
          toast.success(`${verb} the ${confirmPipeline.title.toLowerCase()}`, `${projects[confirmPipeline.projectId]?.name ?? ''} on ${confirmMachine}.`);
        }}
      />
    </PageBody>
  );
}
